import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const GROUP_SEARCH_RESOURCE = "group" as const;
export const GROUP_SEARCH_OPERATION = "search" as const;
export const GROUP_SEARCH_API_PATH = `/v1/${GROUP_SEARCH_RESOURCE}/${GROUP_SEARCH_OPERATION}` as const;

export type GroupSearchParameters = paths[typeof GROUP_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type GroupSearchBody = paths[typeof GROUP_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type GroupSearchResponse =
  paths[typeof GROUP_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useGroupSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<GroupSearchParameters, GroupSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<GroupSearchResponse, Error, GroupSearchResponse, readonly unknown[]>;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<GroupSearchParameters, GroupSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      GROUP_SEARCH_RESOURCE,
      GROUP_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<GroupSearchResponse>(
        GROUP_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
